import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service | GPU Compare</title>
        <meta name="description" content="Terms of Service for GPU Compare - Read our terms and conditions for using our GPU comparison service." />
      </Helmet>
      
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        
        <main className="flex-1 container mx-auto px-4 py-12 max-w-4xl">
          <Link to="/gpu-comparison" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to GPU Comparison
          </Link>
          
          <h1 className="text-3xl font-bold mb-8">Terms of Service</h1>
          <p className="text-muted-foreground mb-8">Last updated: {new Date().toLocaleDateString()}</p>
          
          <div className="prose prose-sm max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-4">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground">
                By accessing or using GPU Compare ("Service"), you agree to be bound by these Terms of Service 
                ("Terms"). If you do not agree to these Terms, please do not use our Service.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">2. Description of Service</h2>
              <p className="text-muted-foreground">
                GPU Compare provides graphics card comparison tools, benchmark data, price tracking, and related 
                information to help users make informed purchasing decisions. Our Service includes price alerts, 
                performance comparisons, and links to third-party retailers.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">3. User Accounts</h2>
              <p className="text-muted-foreground mb-4">
                To access certain features of our Service, you may be required to create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized use</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">4. Affiliate Disclosure</h2>
              <p className="text-muted-foreground">
                GPU Compare participates in affiliate programs with various retailers. When you click on links 
                to retailers on our site and make a purchase, we may earn a commission at no additional cost to 
                you. This helps support our service and allows us to continue providing free content. Our 
                recommendations are based on objective analysis and are not influenced by affiliate relationships.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">5. Accuracy of Information</h2>
              <p className="text-muted-foreground">
                While we strive to provide accurate and up-to-date information, we make no warranties or 
                representations regarding the accuracy, completeness, or reliability of any information on our 
                Service. Benchmark data, prices, and specifications are subject to change and may vary based on 
                testing conditions, configurations, and market fluctuations.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">6. Intellectual Property</h2>
              <p className="text-muted-foreground">
                All content on GPU Compare, including but not limited to text, graphics, logos, icons, images, 
                and software, is the property of GPU Compare or its content suppliers and is protected by 
                United States and international copyright laws. You may not reproduce, distribute, modify, or 
                create derivative works without our express written consent.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">7. User Conduct</h2>
              <p className="text-muted-foreground mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Use the Service for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to any part of the Service</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Use automated systems to access the Service without permission</li>
                <li>Transmit any viruses or malicious code</li>
                <li>Impersonate any person or entity</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">8. Third-Party Links</h2>
              <p className="text-muted-foreground">
                Our Service contains links to third-party websites and services, including retailer websites. 
                We are not responsible for the content, privacy policies, or practices of these third parties. 
                Your interactions with these websites are governed by their respective terms and policies.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">9. Disclaimer of Warranties</h2>
              <p className="text-muted-foreground">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR 
                IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR 
                PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, 
                ERROR-FREE, OR SECURE.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">10. Limitation of Liability</h2>
              <p className="text-muted-foreground">
                IN NO EVENT SHALL GPU COMPARE, ITS DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, 
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE 
                OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU HAVE PAID US, IF ANY, IN 
                THE PAST TWELVE MONTHS.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">11. Indemnification</h2>
              <p className="text-muted-foreground">
                You agree to indemnify and hold harmless GPU Compare and its affiliates from any claims, 
                damages, losses, liabilities, and expenses arising out of your use of the Service or violation 
                of these Terms.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">12. Termination</h2>
              <p className="text-muted-foreground">
                We reserve the right to terminate or suspend your account and access to the Service at our sole 
                discretion, without notice, for conduct that we believe violates these Terms or is harmful to 
                other users, us, or third parties.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">13. Governing Law</h2>
              <p className="text-muted-foreground">
                These Terms shall be governed by and construed in accordance with the laws of the United States 
                and the State of Delaware, without regard to its conflict of law provisions.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">14. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We reserve the right to modify these Terms at any time. We will notify users of any material 
                changes by posting the updated Terms on this page. Your continued use of the Service after 
                changes constitutes acceptance of the revised Terms.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">15. Contact Information</h2>
              <p className="text-muted-foreground">
                For questions about these Terms, please contact us at legal@gpucompare.com.
              </p>
            </section>
          </div>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default TermsPage;
